import type { CommandDeckContinueResolution } from './resolveCommandDeckContinue'
import type { CommandDeckPreviewBundle, CommandDeckSurfaceMode } from '../contracts/types'

export type WorkContextFieldSource = 'bundle' | 'continue' | 'preview' | 'placeholder'

export type WorkContextRow = {
  id: 'project' | 'surface' | 'position' | 'session'
  label: string
  value: string
  source: WorkContextFieldSource
}

export type WorkContextRecallItem = {
  label: string
  source: 'placeholder' | 'bundle'
}

export type CommandDeckWorkContextModel = {
  sectionTitle: string
  rows: WorkContextRow[]
  nextSafeAction: {
    label: string
    value: string
    source: WorkContextFieldSource
    continueLabel: string | null
  }
  recall: {
    pinnedLabel: string
    pinned: WorkContextRecallItem[]
    favouritesLabel: string
    favourites: WorkContextRecallItem[]
  }
  libraryAction: {
    label: string
    detailLabel: string
    navigable: boolean
    href: string | null
    source: WorkContextFieldSource
  }
  contextFootnote: string
}

const PLACEHOLDER_PINNED: WorkContextRecallItem[] = [
  { label: 'Pinned items planned', source: 'placeholder' },
]

const PLACEHOLDER_FAVOURITES: WorkContextRecallItem[] = [
  { label: 'Favourites planned', source: 'placeholder' },
]

function resolveFocusStageLabel(bundle: CommandDeckPreviewBundle): string {
  const { currentFocusStageId, spine } = bundle.progress
  return spine.find((s) => s.stageId === currentFocusStageId)?.label ?? 'In progress'
}

function resolveSurfaceLabel(bundle: CommandDeckPreviewBundle): string {
  const { surface } = bundle.personalization
  return surface.surfaceId !== 'none' ? surface.label : 'Unresolved'
}

function resolveSessionLabel(
  bundle: CommandDeckPreviewBundle,
  mode: CommandDeckSurfaceMode
): { value: string; source: WorkContextFieldSource } {
  if (mode === 'preview') {
    return { value: 'Preview context', source: 'preview' }
  }

  const summaryHeadline = bundle.progress.summary?.headline?.trim()
  if (summaryHeadline) {
    return { value: summaryHeadline, source: 'bundle' }
  }

  const lastTouched = bundle.personalization.mission.lastTouchedAt?.trim()
  if (lastTouched) {
    return { value: `Current session (last touched ${lastTouched})`, source: 'bundle' }
  }

  return {
    value: 'Current session — session history not connected yet',
    source: 'placeholder',
  }
}

function resolveNextSafeAction(
  bundle: CommandDeckPreviewBundle,
  continueResolution: CommandDeckContinueResolution
): CommandDeckWorkContextModel['nextSafeAction'] {
  const suggested = bundle.bestNextMove.primary.suggestedAction.trim()
  const value = suggested.length > 0 ? suggested : 'Review the recommended next move in context'
  return {
    label: 'Next safe action',
    value,
    source: 'bundle',
    continueLabel: continueResolution.label || null,
  }
}

export function deriveCommandDeckWorkContext(input: {
  bundle: CommandDeckPreviewBundle
  mode: CommandDeckSurfaceMode
  continueResolution: CommandDeckContinueResolution
}): CommandDeckWorkContextModel {
  const { bundle, mode, continueResolution } = input
  const session = resolveSessionLabel(bundle, mode)

  return {
    sectionTitle: 'Current Work Context',
    rows: [
      {
        id: 'project',
        label: 'Project',
        value: bundle.progress.projectName,
        source: 'bundle',
      },
      {
        id: 'surface',
        label: 'Surface',
        value: resolveSurfaceLabel(bundle),
        source: 'bundle',
      },
      {
        id: 'position',
        label: 'Last position',
        value: resolveFocusStageLabel(bundle),
        source: 'bundle',
      },
      {
        id: 'session',
        label: 'Last session',
        value: session.value,
        source: session.source,
      },
    ],
    nextSafeAction: resolveNextSafeAction(bundle, continueResolution),
    recall: {
      pinnedLabel: 'Pinned',
      pinned: PLACEHOLDER_PINNED,
      favouritesLabel: 'Favourites',
      favourites: PLACEHOLDER_FAVOURITES,
    },
    libraryAction: {
      label: 'Open Library',
      detailLabel: 'Library connection pending',
      navigable: false,
      href: null,
      source: 'placeholder',
    },
    contextFootnote:
      mode === 'preview'
        ? 'Preview context — pinned items and Library are planned; no saved session memory is implied.'
        : 'Pinned items and Library session details are planned — only bundle-derived context is live today.',
  }
}
